import { Form, FormikHelpers } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validations } from '../../utils/validations';
import { LoginContainer, LoginWrapper } from '../auth/LoginStyles';
import * as Yup from 'yup';
import { Input } from '../common/form/Input';
import { SubmitButton } from '../common/form/SubmitButton';
import { useAppDispatch } from '../../store';
import { TourniquetDialog } from './TourniquetDialog';
import { tourniquetIdentityActions } from '../../store/entities/tourniquetIdentity';
import { tourniquetTrainingsActions } from '../../store/entities/tourniquetTrainings';
import { visitationService } from '@api/services/services';
import { TourniquetTitle } from './TourniquetTitle';
import { VisitationEntryDto } from '@api/models/visitations/VisitationEntryDto';
import { VisitationType } from '@api/models/enums/VisitationType';
import { ShowError } from '../ShowError';
import { apiMessagesStore } from '@api/utils/ApiMessagesStore';
import { resources } from '@api/resources';

interface VisitationAddForm {
  pin: string;
}

export const TourniquetEnter: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [exitModalIsOpen, setExitModalIsOpen] = useState(false);

  const handleSubmit = async (data: VisitationAddForm, formikHelpers: FormikHelpers<VisitationAddForm>) => {
    formikHelpers.resetForm();

    const res = await visitationService.readPin({ pin: data.pin }, true);

    //The visitor has not left yet, so leaving is the only action they may take.
    if (res.data.visitation) {
      const openInvitationId = res.data.visitation.invitation?.id;
      if (!openInvitationId) {
        apiMessagesStore.setError(resources.readPin, 'errorCodes.069');
        return;
      }

      await visitationService.visit({ invitation: openInvitationId, pin: data.pin });
      setExitModalIsOpen(true);
      return;
    }

    const { supplierEmployeeId, invitations } = res.data;

    //Several invitations: the visitor picks one, and only then is it worth loading its trainings.
    if (invitations.length !== 1) {
      dispatch(
        tourniquetIdentityActions.success({
          pin: data.pin,
          invitations,
          employeeId: supplierEmployeeId,
        }),
      );
      return;
    }

    const entryDto: VisitationEntryDto = {
      invitation: invitations[0].id!,
      pin: data.pin,
    };

    const incompleteTrainingsRes = await visitationService.incompleteTrainings(entryDto);
    const validateEntry = await visitationService.accessPointValidateEntry(entryDto);

    //The trainings have to be in the store before the invitation screen renders on them.
    dispatch(tourniquetTrainingsActions.success(incompleteTrainingsRes.data));
    dispatch(
      tourniquetIdentityActions.success({
        pin: data.pin,
        invitations,
        invitation: invitations[0],
        employeeId: supplierEmployeeId,
        validationErrors: validateEntry.data,
      }),
    );
  };

  const handleModalClose = () => {
    setExitModalIsOpen(false);
  };

  return (
    <>
      <TourniquetDialog mode={exitModalIsOpen ? VisitationType.Exit : undefined} onClose={handleModalClose} />

      <LoginWrapper>
        <TourniquetTitle />
        <FormikDebounce<VisitationAddForm>
          initialValues={{
            pin: '',
          }}
          validationSchema={Yup.object().shape({
            pin: validations.stringRequired(t),
          })}
          onSubmit={handleSubmit}
        >
          <Form>
            <LoginContainer>
              <Input type="number" name="pin" label={t('tourniquet.pin')} required autoComplete="off" />
              <SubmitButton text="common.confirm" />
              <ShowError resource={[resources.readPin, resources.accessPointValidateEntry, resources.visit]} />
            </LoginContainer>
          </Form>
        </FormikDebounce>
      </LoginWrapper>
    </>
  );
};
